import React, { useState, useEffect } from "react";
import {
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Input
} from "@material-ui/core";
import { ButtonMenu } from "@loanbossinc/component-library";
import { Add, Delete } from "@material-ui/icons";
import { AutoCompleteInput } from "components/Global/FormikMaterial";
import { loanProceedsSpreadTerms } from "components/Calculators/Constants/DropDownLists";
import { LoanProceedsConstraintsModal } from "modals";
import { Formik } from "formik";
import inputComponentMask from "lib/inputMask";
import { v4 as uuidv4 } from "uuid";
import styled from "styled-components";

import { mapOrder, groupBy } from "../..";
import "./styles.css";

const TOTAL_WIDTH = 1360;
const NUM_OF_COLUMNS = 12;

const StyledInput = styled(Input)`
    text-align-last: center;
`;

const CONSTRAINT_MODAL_TYPE = {
  ADD: "Add",
  EDIT: "Edit"
};

const EmptyRow = () => <>
  <TableCell align="left" style={{ width: `${TOTAL_WIDTH / NUM_OF_COLUMNS}px` }}><AddButtonWithText /></TableCell>
  {Array.from({ length: NUM_OF_COLUMNS }).fill(<TableCell style={{ width: `${TOTAL_WIDTH / NUM_OF_COLUMNS}px` }} />)}
</>;

const AddButtonWithText = ({ onClick }) => (
  <Button style={{ alignItems: "center" }} onClick={onClick}>
    <div className="spread-matrices-cell-add-wrapper">
      <span><Add className="spread-matrices-cell-add-icon" /></span>
      <span className="spread-matrices-cell-add-text">Add</span>
    </div>
  </Button>
);

const SpreadMatrices = ({
  setFieldValue,
  constraintsByLoanType,
  spreadsByLoanTypeSettingId,
  constraints,
  setConstraints,
  spreads,
  setSpreads
}) => {
  const [spreadTermOptions, setSpreadTermOptions] = useState(loanProceedsSpreadTerms);
  const [constraintsModalIsVisible, setConstraintsModalIsVisible] = useState(false);
  const [constraintsModalType, setConstraintsModalType] = useState(CONSTRAINT_MODAL_TYPE.ADD);
  const initialValues = {
    constraintId: "",
    lpscMinDscr: "",
    lpscMaxLtv: "",
    lpscMinDebtYield: ""
  };

  const refreshSpreadTermOptionsAvailable = _spreads => {
    const spreadTerms = _spreads?.map(({ term }) => term);
    setSpreadTermOptions(loanProceedsSpreadTerms.filter(({ value }) => !spreadTerms.includes(value)));
  };

  const constructSpread = _spreads => {
    const constraintsLength = constraints?.length;
    const sortedConstraintsIds = constraints?.sort((a, b) => b.ltv - a.ltv)?.map(({ constraintId }, idx) => ({ constraintId, order: idx }));

    return _spreads.map(_spread => {
      const { amount } = _spread;

      // Fill in empty spreads from missing constraints
      const missingConstraintSpreads = [];
      if (constraintsLength > amount.length) {
        const constraintIds = constraints?.map(({ constraintId }) => constraintId);
        const amountConstraintIds = amount.map(({ underwritingConstraintId }) => underwritingConstraintId);

        const missingConstraints = constraintIds.filter(id => !amountConstraintIds.includes(id));
        const artificialSpreads = missingConstraints.map(_constraint => ({
          spread: 0,
          constraintId: _constraint,
          constraintOrder: sortedConstraintsIds.find(constraint => constraint.constraintId === _constraint).order,
          term: _spread.term,
          creditAdjustment: 0
        }));

        missingConstraintSpreads.push({ ..._spread, amount: [...artificialSpreads] });
      }

      const spreadsWithExistingConstraints = ({
        ..._spread,
        amount: _spread?.amount?.map(({ spread: s, underwritingConstraintId }) => ({
          constraintId: underwritingConstraintId,
          constraintOrder: sortedConstraintsIds.find(constraint => constraint?.constraintId === underwritingConstraintId)?.order,
          spread: s,
          term: _spread.term,
          creditAdjustment: _spread.creditAdjustment
        }))
      });

      const missingConstraintAmounts = missingConstraintSpreads?.length > 0 ? missingConstraintSpreads?.flatMap(({ amount }) => amount) : [];

      return ({
        ...spreadsWithExistingConstraints,
        amount: [
          ...spreadsWithExistingConstraints.amount,
          ...missingConstraintAmounts
        ]
          .filter(({ constraintId }) => constraintId !== undefined)
          .sort((a, b) => a.constraintOrder - b.constraintOrder)
      });
    });
  };

  useEffect(() => {
    const uniqueSpreadsByLoanTypeSettingId = [...new Map(spreadsByLoanTypeSettingId.map(v => [v.id, v])).values()];
    const groupedSpreads = groupBy(uniqueSpreadsByLoanTypeSettingId, s => s.termMonths);

    const newSpreads = Array.from(groupedSpreads, ([name, value]) => ({
      term: name,
      loanProceedsSpreads: value,
      spreadRowId: uuidv4(),
      creditAdjustment: value[0].loanProceedsCreditAdjustment?.spreadAdjustment ?? 0,
      amount: [...value]
    }));

    const constructedSpreads = constructSpread(newSpreads);

    const newSpreadsWithAllConstraints = constructedSpreads.map(y => ({
      ...y,
      amount: y.amount.map(z => {
        const spreadId = y?.loanProceedsSpreads?.find(x => x.underwritingConstraintId === z.constraintId);

        return ({
          ...z,
          spreadId: spreadId?.id
        });
      })
    })).sort((a, b) => a.term - b.term);

    refreshSpreadTermOptionsAvailable(newSpreadsWithAllConstraints);
    setSpreads(newSpreadsWithAllConstraints);
  }, [spreadsByLoanTypeSettingId]);

  useEffect(() => {
    setFieldValue("lpsConstraints", constraints);
  }, [constraints, spreads]);

  useEffect(() => {
    const sortedConstraints = constraintsByLoanType.sort((a, b) => b.ltvMax - a.ltvMax);

    setConstraints(sortedConstraints.map(({ id, ltvMax, debtYieldMin, dscrMin }) => ({
      constraintId: id,
      ltv: ltvMax,
      dy: debtYieldMin,
      dscr: dscrMin
    })));
  }, [constraintsByLoanType]);

  const ButtonMenuOptions = (id, formikProps) => [
    {
      label: "Delete Constraint",
      id: "deleteConstraint",
      onClick: () => {
        handleDeleteConstraint(id);
      }
    },
    {
      label: "Edit Constraint",
      id: "editConstraint",
      onClick: () => {
        handleEditConstraintClicked(id, formikProps);
      }
    }
  ];

  const handleDeleteTerm = (id, term) => {
    const newSpreadOptions = [
      ...spreadTermOptions,
      {
        name: `${term} Years`,
        label: `${term} Years`,
        value: term
      }
    ]
      .sort((a, b) => a.value - b.value);
    setSpreadTermOptions(newSpreadOptions);
    setSpreads(spreads?.filter(({ spreadRowId }) => spreadRowId !== id));
  };

  const handleDeleteConstraint = id => {
    const constraintIndex = constraints.findIndex(constraint => constraint.constraintId === id);
    setConstraints(constraints.filter(constraint => constraint.constraintId !== id));

    const newSpreads = spreads?.map(spread => {
      if (constraintIndex > -1) {
        spread.amount.splice(constraintIndex, 1);

        return ({ ...spread });
      }

      return spread;
    });
    setSpreads(newSpreads);
  };

  const handleAddConstraintClicked = () => {
    setConstraintsModalType(CONSTRAINT_MODAL_TYPE.ADD);
    setConstraintsModalIsVisible(true);
  };

  const handleEditConstraintClicked = (id, formikProps) => {
    const { setFieldValue } = formikProps;
    const { dscr, ltv, dy } = constraints.find(c => c.constraintId === id);
    setFieldValue("constraintId", id);
    setFieldValue("lpscMinDscr", dscr);
    setFieldValue("lpscMaxLtv", ltv * 100);
    setFieldValue("lpscMinDebtYield", dy * 100);
    setConstraintsModalType(CONSTRAINT_MODAL_TYPE.EDIT);
    setConstraintsModalIsVisible(true);
  };

  const handleConstraintsOKClick = ({ minDscr = 0, maxLtv = 0, minDebtYield = 0, constraintId }, formikProps) => {
    const { setFieldValue } = formikProps;
    const constraint = {
      minDscr: parseFloat(minDscr),
      maxLtv: parseFloat(maxLtv) / 100,
      minDebtYield: parseFloat(minDebtYield) / 100,
      constraintId
    };

    constraintsModalType === CONSTRAINT_MODAL_TYPE.EDIT ? editConstraint(constraint) : addConstraint(constraint);
    setFieldValue("constraintId", "");
    setFieldValue("lpscMinDscr", "");
    setFieldValue("lpscMaxLtv", "");
    setFieldValue("lpscMinDebtYield", "");
    setConstraintsModalIsVisible(false);
  };

  const editConstraint = constraint => {
    const { minDscr, maxLtv, minDebtYield, constraintId } = constraint;
    const selectedConstraint = constraints.find(c => c.constraintId === constraintId);
    const newConstraints = constraints
      .map(constraint => (constraint.constraintId === selectedConstraint.constraintId ?
        { ...constraint, dscr: minDscr, ltv: maxLtv, dy: minDebtYield } :
        constraint)).sort((a, b) => b.ltv - a.ltv);
    const sortedNewConstraintsIds = newConstraints?.map(({ constraintId }, idx) => ({ constraintId, order: idx }));
    updateAddedOrEditedSpreads(sortedNewConstraintsIds);
    setConstraints(newConstraints);
  };

  const addConstraint = ({ minDscr, maxLtv, minDebtYield }) => {
    const newConstraint = {
      constraintId: uuidv4(),
      dscr: minDscr,
      ltv: maxLtv,
      dy: minDebtYield
    };
    const newConstraints = [...constraints, newConstraint].sort((a, b) => b.ltv - a.ltv);
    const sortedNewConstraintsIds = newConstraints?.map(({ constraintId }, idx) => ({ constraintId, order: idx }));
    const { constraintId } = newConstraint;
    updateAddedOrEditedSpreads(sortedNewConstraintsIds, constraintId);
    setConstraints(newConstraints);
  };

  // TODO: Can probably clean this up, also saw some funny activity with editing constraints.
  const updateAddedOrEditedSpreads = (sortedNewConstraintsIds, newConstraintId) => {
    const newSpread = {
      constraintId: newConstraintId,
      spread: 0,
      creditAdjustment: 0,
      constraintOrder: sortedNewConstraintsIds.find(({ constraintId }) => constraintId === newConstraintId)?.order
    };

    const newAmountsArraySorted = spreads?.map(spread => spread?.amount).map(a => {
      const updatedSpread = newConstraintId ? [...a, { ...newSpread }] : [...a];

      return mapOrder(updatedSpread, sortedNewConstraintsIds, "constraintId");
    });

    const newAmountsArraySortedAgain = newAmountsArraySorted
      .map(amountsArray => amountsArray
        .map(amount => ({
          ...amount,
          constraintOrder: sortedNewConstraintsIds.find(({ constraintId }) => constraintId === amount.constraintId)?.order
        }))
        .sort((a, b) => a.constraintOrder - b.constraintOrder));

    const newSpreads = spreads?.map((spread, idx) => ({ ...spread, amount: newAmountsArraySortedAgain[idx] }));
    const newSpreadsWithTerms = newSpreads?.map(spread => ({
      ...spread,
      amount: spread.amount.map(a => ({
        ...a,
        term: spread.term
      }))
    }));

    setSpreads(newSpreadsWithTerms);
  };

  const handleAddTermClicked = () => {
    const newSpreadTermSortedByConstraintIds = constraints?.map(({ constraintId }) => ({ constraintId, spread: 0, creditAdjustment: 0 }));
    const newTermSpreads = [
      ...spreads,
      {
        spreadRowId: uuidv4(),
        term: uuidv4(),
        amount: [...newSpreadTermSortedByConstraintIds]
      }
    ];
    setSpreads(newTermSpreads);
  };

  const handleSpreadValueChange = ({ value }, constraintId, spreadRowId) => {
    const parsedValue = typeof value === "string" ? value.replace("%", "") : 0;
    const newSpreads = spreads?.map(spread => (spread.spreadRowId === spreadRowId ?
      {
        ...spread,
        amount: spread.amount.map(a => ((a.constraintId === constraintId) ?
          { ...a, spread: parseFloat(parsedValue / 100), term: spread.term } :
          { ...a }))
      } :
      { ...spread }));
    setSpreads(newSpreads);
  };

  const handleLoanTermSelection = (term, spreadRowId) => {
    const newSpreads = spreads?.map(spread => (spread.spreadRowId === spreadRowId ?
      {
        ...spread,
        term,
        amount: spread?.amount.map(a => ({ ...a, term }))
      } :
      { ...spread }));
    setSpreads(newSpreads);
    refreshSpreadTermOptionsAvailable(newSpreads);
  };

  const disabledAddTermsButton = () => spreads?.some(({ term }) => (typeof term === "string") || spreadTermOptions.length === 0);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onReset={(initialValues, form) => form.resetForm(initialValues)}
      onSubmit={values => handleConstraintsOKClick(values)}
      render={formikProps => (
        <>
          <LoanProceedsConstraintsModal
            {...formikProps}
            open={constraintsModalIsVisible}
            modalType={constraintsModalType}
            handleCancel={() => setConstraintsModalIsVisible(false)}
            handleSave={values => handleConstraintsOKClick(values, formikProps)}
          />
          <Table className="spread-matrices-table-wrapper">
            <TableHead className="spread-matrices-table-header">
              <TableRow>
                <TableCell className="spread-matrices-table-header-constraint-table-cell" align="center">Constraints</TableCell>
                {
                  constraints ?
                    (<>
                      {constraints?.map(({ constraintId, ltv, dscr, dy }, idx) => <TableCell key={idx} align="left">
                        <div className="spread-matrices-table-header-cell-wrapper">
                          <div>
                            <div>
                              {Math.floor(ltv * 100)}
                              % LTV
                            </div>
                            <div>
                              {dscr}
                              x DSCR
                            </div>
                            <div>
                              {(dy * 100).toFixed(2)}
                              % DY
                            </div>
                          </div>
                          <ButtonMenu iconName="more_vert" options={ButtonMenuOptions(constraintId, formikProps)} />
                        </div>
                      </TableCell>)}
                      <TableCell><AddButtonWithText onClick={handleAddConstraintClicked} /></TableCell>
                     </>) :
                    <EmptyRow />
                }
              </TableRow>
            </TableHead>
            <TableBody className="spread-matrices-body-section-wrapper">
              {spreads?.map((spread, idx) => <>
                {spread?.term !== undefined &&
                  <TableRow key={idx}>
                    <TableCell className="spread-matrices-first-column-body-section">
                      <AutoCompleteInput
                        options={spreadTermOptions}
                        placeholder={`${(spread?.term === 0 || typeof spread?.term === "string") ? "" : spread?.term} Years`}
                        selectValue="value"
                        onChange={e => {
                          handleLoanTermSelection(e?.value || "", spread?.spreadRowId);
                        }}
                        enableLongerWidth
                        width={120}
                      />
                    </TableCell>
                    {spread?.amount?.map(({ constraintId = "", spread: spreadAmount }, _idx) => <TableCell key={`${spreadAmount}-${_idx}`}>
                      <div className="spread-matrices-body-spread-amount-cell">
                        <StyledInput
                          id={`spread-${spread?.spreadRowId}-${constraintId}`}
                          name={`spread-${spread?.spreadRowId}-${constraintId}`}
                          fieldName={`spread-${spread?.spreadRowId}-${constraintId}`}
                          decimals={2}
                          onBlur={({ target }) => handleSpreadValueChange(target, constraintId, spread?.spreadRowId)}
                          value={(spreadAmount * 100).toFixed(2)}
                          suffix="%"
                          inputComponent={inputComponentMask({ mask: "percentmasknf" })}
                        />
                      </div>
                    </TableCell>)}
                    <TableCell>
                      <Button
                        style={{ alignItems: "center" }}
                        onClick={() => {
                          handleDeleteTerm(spread?.spreadRowId, spread?.term);
                        }}
                      >
                        <Delete style={{ color: "#005AA9" }} />
                      </Button>
                    </TableCell>
                  </TableRow>}
              </>)}
              <TableRow>
                <TableCell style={{ borderBottom: 0 }}>
                  <Button
                    style={{ alignItems: "center" }}
                    onClick={() => handleAddTermClicked()}
                    disabled={disabledAddTermsButton()}
                  >
                    <div className="spread-matrices-cell-add-wrapper">
                      <span><Add className={`${disabledAddTermsButton() ? "spread-matrices-cell-add-button-disabled" : ""} "spread-matrices-cell-add-icon"`} /></span>
                      <span className={`${disabledAddTermsButton() ? "spread-matrices-cell-add-button-disabled" : ""} "spread-matrices-cell-add-text spread-matrices-add-term"`}>Add Term</span>
                    </div>
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </>
      )}
    />
  );
};

export default SpreadMatrices;
